import { useBlockScroll } from "@helpers/hooks/useBlockScroll";
import { t, Trans } from "@helpers/translate";
import classNames from "classnames";
import React, { FC, HTMLAttributes, ReactNode, useState } from "react";
import Icon, { IconType } from "../Icon";
import { Button } from "../Button";
import { twMerge } from "tailwind-merge";

interface InputModalItemProps {
  onClick?: () => void;
  isActive?: boolean;
  children?: ReactNode;
}

const InputModalItem: FC<InputModalItemProps> = ({
  children,
  onClick,
  isActive = false,
}) => {
  const wrapperClass = classNames(
    "mx-1 my-0.5 p-2",
    "hover:bg-gray-100 rounded cursor-pointer whitespace-nowrap",
    "flex items-center",
    {
      "text-black": !isActive,
      "text-secondary": isActive,
    }
  );

  const checkboxClass = classNames(
    "flex items-center border p-0.5 rounded-full text-gray-400 mr-2",
    {
      "bg-white": !isActive,
      "border-secondary bg-secondary text-white": isActive,
    }
  );

  return (
    <div className={wrapperClass} onClick={onClick}>
      <div className={checkboxClass}>
        <Icon
          icon="check"
          size="xs"
          className={isActive ? "text-white" : "invisible"}
        />
      </div>
      {children}
    </div>
  );
};

interface ModalProps {
  setIsOpen: () => void;
  onReset?: () => void;
  onApply?: () => void;
  value?: string;
  BlockScroll?: boolean;
  showApply?: boolean;
  breakingPoint?: "md" | "lg";
  className?: HTMLAttributes<HTMLDivElement>["className"];
  children?: ReactNode;
  calendarMinWidth?: string;
}

const Modal: FC<ModalProps> = ({
  children,
  value,
  setIsOpen,
  onApply,
  showApply = true,
  BlockScroll = true,
  breakingPoint = "md",
  onReset,
  className,
  calendarMinWidth = "max-content",
}) => {
  if (BlockScroll) useBlockScroll();

  const wrapperCommom =
    "bg-white border shadow-xl rounded-xl py-2 border w-full z-10" as HTMLAttributes<HTMLDivElement>["className"];

  let wrapperClassMobile = "" as HTMLAttributes<HTMLDivElement>["className"];

  if (breakingPoint === "md") {
    wrapperClassMobile =
      `md:absolute max-md:fixed w-full max-md:bottom-0 right-0 left-0 md:py-4 md:mt-1` as HTMLAttributes<HTMLDivElement>["className"];
  }
  if (breakingPoint === "lg") {
    wrapperClassMobile =
      `lg:absolute max-lg:fixed w-full max-lg:bottom-0 right-0 left-0 lg:py-4 lg:mt-1` as HTMLAttributes<HTMLDivElement>["className"];
  }

  const wrapperClass = classNames(
    twMerge(wrapperCommom, wrapperClassMobile, className)
  );

  return (
    <div
      className={`z-10 max-${breakingPoint}:fixed max-${breakingPoint}:left-0 max-${breakingPoint}:right-0 max-${breakingPoint}:bottom-0 ${breakingPoint}:absolute w-full`}
    >
      <button
        onClick={() => setIsOpen()}
        tabIndex={-1}
        className="fixed inset-0 h-full w-full cursor-default"
      ></button>
      <div className={wrapperClass} style={{ minWidth: calendarMinWidth }}>
        {children}

        {showApply && (
          <div className="flex w-full justify-between mt-2 pt-3 border-t border-gray-200 text-right">
            {value && (
              <div
                onClick={() => {
                  setIsOpen();
                  if (onReset) {
                    onReset();
                  }
                }}
                className="text-gray-500 hover:underline cursor-pointer inline-block"
              >
                <Trans id="search.filter.clear">Clear</Trans>
              </div>
            )}
            <Button
              variant="black"
              size="md"
              className={`py-1 inline-block ml-4 ${breakingPoint}:text-sm ${breakingPoint}:px-4`}
              onClick={onApply}
              disabled={!onApply}
            >
              <Trans id="form.InputModal.apply">Apply</Trans>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

interface InputModalProps {
  title?: string;
  value?: string;
  className?: string;
  modalClassName?: string;
  icon?: IconType;
  showErrorView?: boolean;
  setIsGlobalOpen?: (open: boolean) => void;
  onReset?: () => void;
  onApply?: () => void;
  checkIsOpen?: () => boolean;
  showApply?: boolean;
  children?: ReactNode;
  breakingPoint?: "md" | "lg";
  calendarMinWidth?: string;
}

interface InputModalComponents {
  Item: typeof InputModalItem;
}

export const InputModal: FC<InputModalProps> & InputModalComponents = ({
  onReset,
  onApply,
  children,
  title,
  value,
  icon,
  setIsGlobalOpen,
  checkIsOpen,
  showErrorView = false,
  className = "",
  showApply = true,
  breakingPoint = "md",
  modalClassName,
  calendarMinWidth = "max-content",
}) => {
  const borderColor = showErrorView
    ? "border-primary border-2 font-bold"
    : "border-gray-400 border";
  const textColor = showErrorView ? "text-primary" : "";

  const buttonClass = classNames(
    "flex items-center justify-between h-12",
    "px-3 py-3 rounded cursor-pointer",
    "whitespace-nowrap",
    `${borderColor} hover:border-gray-500 hover:text-black`,
    className
  );

  const [isOpenLocal, setIsOpenLocal] = useState(false);

  function isOpen() {
    if (checkIsOpen !== undefined) {
      return checkIsOpen();
    }
    return isOpenLocal;
  }

  function setIsOpen(value: boolean) {
    if (setIsGlobalOpen !== undefined) {
      setIsGlobalOpen(value);
    } else {
      setIsOpenLocal(value);
    }
  }

  return (
    <div className="relative w-full">
      <div className={buttonClass} onClick={() => setIsOpen(!isOpen())}>
        <div className="flex items-center">
          {icon && (
            <Icon icon={icon} size="sm" className={`mr-2 ${textColor}`} />
          )}
          <div className={`${textColor}`} suppressHydrationWarning>
            {value || title}
          </div>
        </div>
        <svg
          className={`${textColor} h-4 w-4 ml-2 ${
            isOpen() ? "transform rotate-180" : ""
          } `}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <title id="GWGLogoTitle">
            {t({
              id: "common.svg.title",
              message: `GoWithGuide`,
            })}
          </title>
          <desc id="GWGLogoDesc">
            <Trans id="common.svg.description">
              Find your perfect tour guide at GoWithGuide. Relax and cherish
              unforgettable tours with loved ones. Create personalized tours for
              a truly memorable trip!
            </Trans>
          </desc>
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
      {isOpen() && (
        <Modal
          showApply={showApply}
          value={value}
          setIsOpen={() => setIsOpen(!isOpen())}
          onReset={onReset}
          BlockScroll={false}
          breakingPoint={breakingPoint}
          className={modalClassName}
          calendarMinWidth={calendarMinWidth}
          onApply={
            onApply
              ? () => {
                  onApply();
                  setIsOpen(false);
                }
              : undefined
          }
        >
          {children}
        </Modal>
      )}
    </div>
  );
};

InputModal.Item = InputModalItem;
